import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import translationES from '../shared/locales/es/translation.json';
import translationEN from '../shared/locales/en/translation.json';
import translationPT from '../shared/locales/pt/translation.json';
import translationIT from '../shared/locales/it/translation.json';
import translationFR from '../shared/locales/fr/translation.json';
import translationCA from '../shared/locales/ca/translation.json';
import translationEU from '../shared/locales/eu/translation.json';

import { config } from './env';
import { getBrandConfig } from './brand';
import { resolveBrandOverride } from './brand-overrides';

// Configuración de recursos de traducción
const resources = {
  es: {
    translation: translationES,
  },
  en: {
    translation: translationEN,
  },
  pt: {
    translation: translationPT,
  },
  it: {
    translation: translationIT,
  },
  fr: {
    translation: translationFR,
  },
  ca: {
    translation: translationCA,
  },
  eu: {
    translation: translationEU,
  },
};

// ---------------------------------------------------------------------------
// Post-procesador de marca (white-label)
// ---------------------------------------------------------------------------
// Sustituye el nombre de producto "Ainovis" embebido en las traducciones por
// el de la marca activa (p. ej. "ALMA") cuando la marca NO es la por defecto.
// Mantiene un único juego de traducciones; la marca se resuelve en runtime a
// partir de VITE_BRAND_NAME, sin duplicar archivos de idioma.
//
// Igualmente sustituye el nombre del asistente "Carolina" por el de la marca
// activa (p. ej. "ASSISTANT"). Igual que con el producto, para la marca por
// defecto (Ainovis) no se realiza ninguna sustitución.
const activeBrand = getBrandConfig(config.BRAND);
const brandProductName = activeBrand.productName;
const brandAssistantName = activeBrand.assistantName;

const brandNamePostProcessor = {
  type: 'postProcessor' as const,
  name: 'brandName',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  process(value: string, _key: string, _options: any, _translator: any): string {
    if (typeof value !== 'string') return value;

    // Override puntual de una marca concreta (ver brand-overrides.ts). Si
    // existe para (brand, lng, key), seusa tal cual y se OMITIÓ la
    // sustitución genérica.
    const override = resolveBrandOverride(
      config.BRAND,
      i18n.language,
      _key,
    );
    if (override != null) return override;

    // Marca por defecto (Ainovis): no hay nada que sustituir.
    if (brandProductName === 'Ainovis' && brandAssistantName === 'Carolina') {
      return value;
    }
    let out = value;
    if (brandProductName !== 'Ainovis') {
      out = out.replace(/Ainovis/g, brandProductName);
    }
    if (brandAssistantName !== 'Carolina') {
      out = out.replace(/Carolina/g, brandAssistantName);
    }
    return out;
  },
};

i18n
  // Detecta el idioma del usuario
  .use(LanguageDetector)
  // Sustituye el nombre de marca en las traducciones según VITE_BRAND_NAME
  .use(brandNamePostProcessor)
  // Pasa la instancia de i18n a react-i18next
  .use(initReactI18next)
  // Inicializa i18next
  .init({
    resources,
    fallbackLng: 'es', // Idioma por defecto si no se detecta ninguno
    // Aplica el post-procesador de marca a TODAS las claves traducidas.
    postProcess: ['brandName'],
    // No definir 'lng' para que LanguageDetector lea el idioma guardado en localStorage
    debug: false, // Cambiar a true para ver logs de depuración
    
    interpolation: {
      escapeValue: false, // React ya hace el escape por defecto
    },

    detection: {
      // Orden de detección del idioma
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Clave para guardar el idioma en localStorage
      lookupLocalStorage: 'i18nextLng',
      // Cache del idioma en localStorage
      caches: ['localStorage'],
    },

    react: {
      useSuspense: false, // Desactivar suspense para evitar problemas con SSR
    },
  });

export default i18n;
